import React, { useState, useEffect, useCallback } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Search, RefreshCw, TrendingUp, Package, Sparkles,
  AlertTriangle, BarChart2, X, Plus,
  ChevronDown, ChevronUp, ChevronsUpDown,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────

function Badge({ children, color = 'gray' }) {
  const cls = {
    gray:    'bg-gray-700/50 text-gray-300 border-gray-600/40',
    amber:   'bg-amber-500/15 text-amber-400 border-amber-500/30',
    rose:    'bg-rose-500/15 text-rose-400 border-rose-500/30',
    indigo:  'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
    emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  }[color] || 'bg-gray-700/50 text-gray-300 border-gray-600/40';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      {children}
    </span>
  );
}

function SortIcon({ field, sortBy, dir }) {
  if (sortBy !== field) return <ChevronsUpDown size={12} className="text-gray-600" />;
  return dir === 'desc'
    ? <ChevronDown size={12} className="text-indigo-400" />
    : <ChevronUp size={12} className="text-indigo-400" />;
}

function ClickRateBar({ rate }) {
  const pct = Math.min(rate, 100);
  const color = pct >= 20 ? 'bg-emerald-500' : pct >= 8 ? 'bg-indigo-500' : pct >= 2 ? 'bg-amber-500' : 'bg-gray-700';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden w-16">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-medium tabular-nums ${pct >= 20 ? 'text-emerald-400' : pct >= 8 ? 'text-indigo-400' : pct >= 2 ? 'text-amber-400' : 'text-gray-600'}`}>
        {rate}%
      </span>
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────

const TABS = [
  { id: 'top',          label: 'Top búsquedas',   icon: TrendingUp },
  { id: 'no_results',   label: 'Sin resultados',   icon: AlertTriangle },
  { id: 'log',          label: 'Log completo',     icon: BarChart2 },
  { id: 'opportunities',label: 'Oportunidades',    icon: Sparkles },
];

// ── Days selector ─────────────────────────────────────────────
const DAY_OPTIONS = [[7,'7 días'],[14,'14 días'],[30,'30 días'],[90,'90 días']];

// ── Main page ─────────────────────────────────────────────────

export default function SearchesPage() {
  const { organization } = useOrganization();
  const orgId = organization?.id;

  const [tab,         setTab]         = useState('top');
  const [days,        setDays]        = useState(30);
  const [loading,     setLoading]     = useState(true);

  // Top queries state
  const [topData,     setTopData]     = useState(null);
  const [sortBy,      setSortBy]      = useState('searches');
  const [sortDir,     setSortDir]     = useState('desc');

  // No-results state
  const [noResultsData, setNoResultsData] = useState(null);

  // Log + opportunities state
  const [logData,     setLogData]     = useState(null);
  const [logMode,     setLogMode]     = useState('all');
  const [logQ,        setLogQ]        = useState('');

  // ── Loaders ────────────────────────────────────────────────

  const loadTop = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const r = await api.get(`/api/analytics/query-stats?organization_id=${orgId}&days=${days}&sort_by=${sortBy}&page_size=200`);
      setTopData(r.data);
    } catch { toast.error('Error al cargar top búsquedas'); }
    finally { setLoading(false); }
  }, [orgId, days, sortBy]);

  const loadNoResults = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const r = await api.get(`/api/analytics/query-stats?organization_id=${orgId}&days=${days}&sort_by=no_results&page_size=200`);
      // Filter: only queries that returned no results at least once
      const filtered = (r.data?.queries || []).filter(q => q.no_results_count > 0);
      // Sort by no_results_count desc, then by no_results_pct
      filtered.sort((a, b) => b.no_results_count - a.no_results_count);
      setNoResultsData({ ...r.data, queries: filtered });
    } catch { toast.error('Error al cargar búsquedas sin resultados'); }
    finally { setLoading(false); }
  }, [orgId, days]);

  const loadLog = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const r = await api.get(`/api/analytics/search-log?organization_id=${orgId}&days=${days}&mode=${logMode}&q=${encodeURIComponent(logQ)}`);
      setLogData(r.data);
    } catch { toast.error('Error al cargar búsquedas'); }
    finally { setLoading(false); }
  }, [orgId, days, logMode, logQ]);

  // Load on tab/day change
  useEffect(() => {
    if (tab === 'top')           loadTop();
    else if (tab === 'no_results') loadNoResults();
    else if (tab === 'log' || tab === 'opportunities') loadLog();
  }, [tab, days, loadTop, loadNoResults, loadLog]);

  const refresh = () => {
    if (tab === 'top')           loadTop();
    else if (tab === 'no_results') loadNoResults();
    else loadLog();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  // Client-side re-sort when dir changes (data already sorted by sortBy from server)
  const topQueries = React.useMemo(() => {
    if (!topData?.queries) return [];
    const arr = [...topData.queries];
    const field = sortBy === 'click_rate' ? 'click_rate'
      : sortBy === 'clicks' ? 'clicks'
      : sortBy === 'conversion_rate' ? 'conversion_rate'
      : sortBy === 'conversions' ? 'conversions'
      : 'searches';
    arr.sort((a, b) => sortDir === 'desc' ? b[field] - a[field] : a[field] - b[field]);
    return arr;
  }, [topData, sortBy, sortDir]);

  const totalOpps = (logData?.product_opportunities?.length || 0) + (logData?.synonym_opportunities?.length || 0);

  // ── Header ──────────────────────────────────────────────────
  return (
    <div data-testid="searches-page" className="p-6 max-w-6xl mx-auto space-y-5">

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart2 size={22} className="text-indigo-400" /> Búsquedas
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Analiza qué buscan tus clientes y detecta oportunidades</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            data-testid="days-select"
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
          >
            {DAY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button
            onClick={refresh}
            data-testid="refresh-btn"
            className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm border border-gray-700 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-800">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === id
                ? 'border-indigo-500 text-white'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            <Icon size={13} />
            {label}
            {id === 'opportunities' && totalOpps > 0 && (
              <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{totalOpps}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab: Top Búsquedas ──────────────────────────────── */}
      {tab === 'top' && (
        <div data-testid="top-queries-tab">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">

            {/* Table header */}
            <div className="px-5 py-3 border-b border-gray-800 grid grid-cols-12 text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              <span className="col-span-1">#</span>
              <span className="col-span-3">Query</span>
              <button
                onClick={() => handleSort('searches')}
                data-testid="sort-btn-searches"
                className="col-span-2 flex items-center justify-center gap-1 hover:text-gray-300 transition-colors cursor-pointer"
              >
                Búsquedas <SortIcon field="searches" sortBy={sortBy} dir={sortDir} />
              </button>
              <button
                onClick={() => handleSort('clicks')}
                data-testid="sort-btn-clicks"
                className="col-span-2 flex items-center justify-center gap-1 hover:text-gray-300 transition-colors cursor-pointer"
              >
                Clics <SortIcon field="clicks" sortBy={sortBy} dir={sortDir} />
              </button>
              <button
                onClick={() => handleSort('click_rate')}
                data-testid="sort-btn-click-rate"
                className="col-span-2 flex items-center justify-center gap-1 hover:text-gray-300 transition-colors cursor-pointer"
              >
                Conv. clic <SortIcon field="click_rate" sortBy={sortBy} dir={sortDir} />
              </button>
              <button
                onClick={() => handleSort('conversion_rate')}
                data-testid="sort-btn-conversion-rate"
                className="col-span-1 flex items-center justify-center gap-1 hover:text-gray-300 transition-colors cursor-pointer"
              >
                Conv. venta <SortIcon field="conversion_rate" sortBy={sortBy} dir={sortDir} />
              </button>
              <span className="col-span-1 text-right">% sin res.</span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <RefreshCw size={16} className="animate-spin mr-2" /> Cargando...
              </div>
            ) : topQueries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-600">
                <Search size={28} className="mb-2 opacity-40" />
                <p className="text-sm">No hay búsquedas registradas en este periodo</p>
                <p className="text-xs mt-1">Instala el widget para empezar a registrar</p>
              </div>
            ) : topQueries.map((row, i) => (
              <div
                key={row.query}
                data-testid="top-query-row"
                className="px-5 py-3.5 grid grid-cols-12 items-center border-b border-gray-800/50 hover:bg-gray-800/25 transition-colors"
              >
                {/* Rank */}
                <span className="col-span-1 text-gray-600 text-sm font-mono">{i + 1}</span>

                {/* Query */}
                <div className="col-span-3 flex items-center gap-2 min-w-0">
                  <span className="text-sm text-white font-medium truncate">"{row.query}"</span>
                  {row.no_results_pct === 100 && <Badge color="rose">Sin resultados</Badge>}
                </div>

                {/* Searches */}
                <div className="col-span-2 flex justify-center">
                  <span className="text-sm text-gray-200 font-semibold tabular-nums">{row.searches.toLocaleString()}</span>
                </div>

                {/* Clicks */}
                <div className="col-span-2 flex justify-center">
                  <span className="text-sm text-gray-400 tabular-nums">{row.clicks.toLocaleString()}</span>
                </div>

                {/* Click rate bar */}
                <div className="col-span-2 flex justify-center">
                  <ClickRateBar rate={row.click_rate} />
                </div>

                {/* Sale conversion rate */}
                <div className="col-span-1 flex justify-center">
                  {row.conversion_rate > 0 ? (
                    <span className="text-xs font-semibold text-emerald-400 tabular-nums">{row.conversion_rate}%</span>
                  ) : (
                    <span className="text-xs text-gray-700">—</span>
                  )}
                </div>

                {/* No results % */}
                <div className="col-span-1 flex justify-end">
                  <span className={`text-xs font-medium tabular-nums ${
                    row.no_results_pct >= 80 ? 'text-rose-400' :
                    row.no_results_pct >= 30 ? 'text-amber-400' :
                    row.no_results_pct > 0   ? 'text-gray-400' : 'text-emerald-400'
                  }`}>
                    {row.no_results_pct > 0 ? `${row.no_results_pct}%` : '—'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {topData && (
            <p className="text-xs text-gray-600 text-right mt-2">
              {topData.total} queries únicas en {days} días
            </p>
          )}
        </div>
      )}

      {/* ── Tab: Sin Resultados ─────────────────────────────── */}
      {tab === 'no_results' && (
        <div data-testid="no-results-tab">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <AlertTriangle size={14} className="text-rose-400" />
                  Búsquedas sin resultados
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Términos que tus clientes buscan y no encuentran en tu catálogo
                </p>
              </div>
              {noResultsData && (
                <Badge color="rose">{noResultsData.queries?.length || 0} queries</Badge>
              )}
            </div>

            {/* Column headers */}
            <div className="px-5 py-3 border-b border-gray-800 grid grid-cols-12 text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              <span className="col-span-1">#</span>
              <span className="col-span-5">Query</span>
              <span className="col-span-2 text-center">Veces sin result.</span>
              <span className="col-span-2 text-center">Total búsquedas</span>
              <span className="col-span-1 text-center">% sin result.</span>
              <span className="col-span-1 text-right">Última</span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <RefreshCw size={16} className="animate-spin mr-2" /> Cargando...
              </div>
            ) : (noResultsData?.queries || []).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-600">
                <span className="text-3xl mb-3">🎉</span>
                <p className="text-sm font-medium text-gray-400">¡Tu catálogo cubre todas las búsquedas!</p>
                <p className="text-xs mt-1">No hay términos sin resultados en los últimos {days} días</p>
              </div>
            ) : (noResultsData.queries).map((row, i) => (
              <div
                key={row.query}
                data-testid="no-result-row"
                className="px-5 py-3.5 grid grid-cols-12 items-center border-b border-gray-800/50 hover:bg-gray-800/25 transition-colors"
              >
                <span className="col-span-1 text-gray-600 text-sm font-mono">{i + 1}</span>
                <div className="col-span-5 flex items-center gap-2 min-w-0">
                  <span className="text-sm text-white font-medium truncate">"{row.query}"</span>
                  {row.no_results_pct === 100 && <Badge color="rose">Solo sin resultados</Badge>}
                </div>
                <span className="col-span-2 text-center text-sm font-semibold text-rose-400 tabular-nums">
                  {row.no_results_count.toLocaleString()}
                </span>
                <span className="col-span-2 text-center text-sm text-gray-400 tabular-nums">
                  {row.searches.toLocaleString()}
                </span>
                <span className={`col-span-1 text-center text-sm font-medium tabular-nums ${
                  row.no_results_pct >= 80 ? 'text-rose-400' : 'text-amber-400'
                }`}>
                  {row.no_results_pct}%
                </span>
                <span className="col-span-1 text-right text-[11px] text-gray-600">
                  {row.last_searched}
                </span>
              </div>
            ))}
          </div>

          {(noResultsData?.queries?.length || 0) > 0 && (
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <p className="text-xs text-amber-300 font-medium mb-1">
                Acciones recomendadas
              </p>
              <ul className="text-xs text-amber-200/70 space-y-1 list-disc list-inside">
                <li>Añade estos términos como <strong>sinónimos</strong> de productos existentes</li>
                <li>Considera añadir estos productos a tu catálogo si tienen alta frecuencia</li>
                <li>Revisa si hay errores ortográficos que puedas cubrir con sinónimos</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Log completo ────────────────────────────────── */}
      {tab === 'log' && (
        <div data-testid="log-tab">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                value={logQ}
                onChange={e => setLogQ(e.target.value)}
                placeholder="Filtrar por query..."
                className="w-full pl-8 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
              />
              {logQ && (
                <button onClick={() => setLogQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="flex gap-1 bg-gray-800 border border-gray-700 rounded-lg p-1">
              {[['all','Todas'],['no_results','Sin resultados'],['opportunities','Oportunidades']].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => setLogMode(v)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${logMode === v ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-800 grid grid-cols-12 text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              <span className="col-span-5">Query</span>
              <span className="col-span-2 text-center">Veces</span>
              <span className="col-span-2 text-center">Avg. resultados</span>
              <span className="col-span-2 text-center">% sin resultado</span>
              <span className="col-span-1 text-right">Última</span>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <RefreshCw size={16} className="animate-spin mr-2" /> Cargando...
              </div>
            ) : (logData?.queries || []).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-600">
                <Search size={24} className="mb-2 opacity-40" />
                <p className="text-sm">No hay búsquedas en este periodo</p>
              </div>
            ) : (logData.queries).map((row, i) => (
              <div key={i} className="px-5 py-3 grid grid-cols-12 items-center border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                <div className="col-span-5 flex items-center gap-2">
                  <span className="text-sm text-white font-medium">"{row.query}"</span>
                  {row.avg_results === 0 && <Badge color="rose"><AlertTriangle size={9} /> Sin resultados</Badge>}
                  {row.avg_results > 0 && row.avg_results < 4 && <Badge color="amber">Pocos resultados</Badge>}
                </div>
                <span className="col-span-2 text-center text-sm text-gray-300 font-semibold">{row.count}</span>
                <span className="col-span-2 text-center text-sm text-gray-400">{row.avg_results}</span>
                <span className={`col-span-2 text-center text-sm font-medium ${row.zero_pct > 50 ? 'text-rose-400' : row.zero_pct > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {row.zero_pct}%
                </span>
                <span className="col-span-1 text-right text-[11px] text-gray-600">{row.last_searched}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab: Oportunidades ───────────────────────────────── */}
      {tab === 'opportunities' && (
        <div data-testid="opportunities-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Product opportunities */}
          <OpportunityCard
            title="Oportunidades de producto"
            icon={Package}
            color="bg-rose-500/15 text-rose-400"
            items={logData?.product_opportunities || []}
            emptyText="¡Tu catálogo cubre bien la demanda!"
            actionLabel="Ver catálogo"
            onAction={() => window.location.href = '/search-engine'}
          />
          {/* Synonym opportunities */}
          <OpportunityCard
            title="Oportunidades de sinónimos"
            icon={Sparkles}
            color="bg-amber-500/15 text-amber-400"
            items={logData?.synonym_opportunities || []}
            emptyText="No hay términos frecuentes con pocos resultados."
            actionLabel="Añadir sinónimo"
            onAction={(item) => window.location.href = `/search-engine?synonym=${encodeURIComponent(item.query)}`}
          />
        </div>
      )}
    </div>
  );
}

// ── Opportunity card (shared) ─────────────────────────────────

function OpportunityCard({ title, icon: Icon, color, items, emptyText, actionLabel, onAction }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-800 flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={16} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <p className="text-xs text-gray-500">{items.length} término{items.length !== 1 ? 's' : ''}</p>
        </div>
      </div>
      <div className="divide-y divide-gray-800/50">
        {items.length === 0 ? (
          <div className="px-5 py-6 text-center text-gray-600 text-sm">{emptyText}</div>
        ) : items.map((item, i) => (
          <div key={i} className="px-5 py-3 flex items-center justify-between hover:bg-gray-800/30 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white truncate">"{item.query}"</span>
                <Badge color="gray">{item.count}x</Badge>
                {item.avg_results !== undefined && item.avg_results > 0 && (
                  <Badge color="indigo">~{item.avg_results} resultados</Badge>
                )}
              </div>
              {item.last_searched && (
                <p className="text-[11px] text-gray-600 mt-0.5">Última: {item.last_searched}</p>
              )}
            </div>
            {onAction && (
              <button
                onClick={() => onAction(item)}
                className="ml-3 flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 transition-colors flex-shrink-0"
              >
                <Plus size={11} /> {actionLabel}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
